// Work Step spreadsheet import: mapping suggestions + candidate building.
// File parsing reuses the entity-agnostic parseRcmFile from rcmParser.
import type { RcmParsed, RcmRow } from "./rcmParser";

export type WorkStepMapping = {
  name?: string;
  description?: string;
  section?: string;
  subsection?: string;
  preparer?: string;
  opsAudit?: string;
};

const textToString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number")
    return Number.isFinite(value) ? `${value}` : "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return `${value}`;
};

const normalizeHeader = (header: string) =>
  header
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

type Synonym = { tokens: string[]; weight?: number } | string;

const syn = (value: Synonym): { tokens: string[]; weight: number } =>
  typeof value === "string"
    ? { tokens: [value], weight: 1 }
    : { tokens: value.tokens, weight: value.weight ?? 1 };

// Includes AuditBoard's own export vocabulary (title, procedures, section,
// preparer, audit) so an ops_audit_items export maps with no manual work.
const FIELD_SYNONYMS: Record<keyof Required<WorkStepMapping>, Synonym[]> = {
  name: [
    { tokens: ["work step name"], weight: 2 },
    { tokens: ["work step title"], weight: 2 },
    { tokens: ["title"], weight: 2 },
    "work step",
    "workstep",
    "name",
    "step name",
  ],
  description: [
    { tokens: ["description"], weight: 2 },
    "work step description",
    "procedures",
    "objective",
    "narrative",
  ],
  section: [{ tokens: ["section"], weight: 2 }, "section name", "phase"],
  subsection: [
    { tokens: ["subsection"], weight: 2 },
    "sub section",
    "sub-section",
    "subsection name",
  ],
  preparer: [
    { tokens: ["preparer"], weight: 2 },
    "prepared by",
    "assignee",
    "owner",
    "responsible party",
  ],
  opsAudit: [
    { tokens: ["opsaudit"], weight: 2 },
    { tokens: ["ops audit"], weight: 2 },
    { tokens: ["audit"], weight: 2 },
    "audit name",
    "audit title",
    "internal audit",
    "project",
    "engagement",
  ],
};

const scoreHeader = (header: string, synonyms: Synonym[]): number => {
  const norm = normalizeHeader(header);
  let best = 0;
  for (const synItem of synonyms) {
    const { tokens, weight } = syn(synItem);
    for (const token of tokens) {
      const t = normalizeHeader(token);
      if (norm === t) {
        best = Math.max(best, 100 * weight);
      } else if (norm.includes(t)) {
        best = Math.max(best, 5 * weight);
      } else if (t.includes(norm)) {
        best = Math.max(best, 3 * weight);
      }
    }
  }
  return best;
};

// Object.keys() widens to string[]; FIELD_SYNONYMS' key set is exactly the
// mapping's fields (enforced by its Record<> type), so narrowing by membership
// keeps the iteration typed without an assertion.
const isMappingField = (key: string): key is keyof WorkStepMapping =>
  key in FIELD_SYNONYMS;

export function suggestWorkStepMapping(headers: string[]): WorkStepMapping {
  const mapping: WorkStepMapping = {};
  const used = new Set<string>();

  Object.keys(FIELD_SYNONYMS)
    .filter(isMappingField)
    .forEach((field) => {
      let bestHeader: string | null = null;
      let bestScore = 0;
      for (const h of headers) {
        if (used.has(h)) continue;
        const s = scoreHeader(h, FIELD_SYNONYMS[field]);
        if (s > bestScore) {
          bestScore = s;
          bestHeader = h;
        }
      }
      if (bestHeader) {
        mapping[field] = bestHeader;
        used.add(bestHeader);
      }
    });

  return mapping;
}

export type WorkStepCandidate = {
  name: string;
  description: string;
  section?: string;
  subsection?: string;
  preparer?: string;
  // Raw OpsAudit name from the mapped column; empty when the column is
  // unmapped or the cell is blank — those rows take the flow's default.
  opsAuditName?: string;
};

export function buildWorkStepCandidates(
  parsed: RcmParsed,
  mapping: WorkStepMapping,
): WorkStepCandidate[] {
  const readCell = (row: RcmRow, header: string | undefined): string =>
    textToString(header ? row[header] : "").trim();

  return parsed.rows
    .map((row) => {
      const name = readCell(row, mapping.name);
      const description = readCell(row, mapping.description);
      const section = readCell(row, mapping.section);
      const subsection = readCell(row, mapping.subsection);
      const preparer = readCell(row, mapping.preparer);
      const opsAuditName = readCell(row, mapping.opsAudit);
      if (
        !name &&
        !description &&
        !section &&
        !subsection &&
        !preparer &&
        !opsAuditName
      ) {
        return null;
      }
      const candidate: WorkStepCandidate = {
        name,
        description,
        ...(section && { section }),
        ...(subsection && { subsection }),
        ...(preparer && { preparer }),
        ...(opsAuditName && { opsAuditName }),
      };
      return candidate;
    })
    .filter((candidate) => candidate !== null);
}
